import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { TicketDetail } from "@signalist/shared-types";

import { TicketStatusPill } from "../../../components/TicketPills";
import { buildTicketThread, ticketUserFirstName } from "../utils/ticketThread";
import { TicketMessage } from "./TicketMessage";
import { TicketSystemLine } from "./TicketSystemLine";
import { TicketTranscript } from "./TicketTranscript";

interface TicketThreadProps {
	ticket: TicketDetail;
	onAttachmentError: (fileName: string) => void;
}

/** The visible thread in comment order; body-level files ride on the first message (the description). */
export const TicketThread: FunctionComponent<TicketThreadProps> = ({
	ticket,
	onAttachmentError,
}) => {
	const { t } = useTranslation();
	const items = buildTicketThread(ticket);
	const firstMessageId = items.find((item) => item.kind === "message")?.comment.id;

	return (
		<div className="flex flex-col gap-4">
			{items.map((item) => {
				switch (item.kind) {
					case "message":
						return (
							<TicketMessage
								key={item.comment.id}
								comment={item.comment}
								extraAttachments={item.comment.id === firstMessageId ? ticket.attachments : []}
								isStaff={item.isStaff}
								onAttachmentError={onAttachmentError}
							/>
						);
					case "transcript":
						return (
							<TicketTranscript
								key={item.comment.id}
								customerName={ticketUserFirstName(ticket.createdBy)}
								entries={item.entries}
							/>
						);
					case "received":
						return (
							<TicketSystemLine key={item.id}>
								<span>{t("support.detail.thread.received")}</span>
							</TicketSystemLine>
						);
					case "status":
						return (
							<TicketSystemLine key={item.id}>
								<span>{t("support.detail.thread.statusChanged")}</span>
								<TicketStatusPill status={item.to} />
							</TicketSystemLine>
						);
				}
			})}
		</div>
	);
};
